/**
 * Tooltip Component - Smart auto-positioning tooltip with portal rendering
 *
 * Refactored to support:
 * - Portal rendering via createPortal into document.body, escaping any
 *   overflow:hidden / overflow-y:auto ancestor containers
 * - JS-based positioning using getBoundingClientRect for pixel-perfect placement
 * - Auto-flip logic: tries preferred direction, then opposite, then top/bottom/left/right
 *   in order until a direction fits fully within the viewport
 * - Viewport-clamping fallback so the tooltip is always fully on-screen
 * - Elastic bounce animation matched to the effective render direction (after auto-flip)
 * - Backward-compatible API: no changes required at any usage site
 *
 * Updated: onBlur now inspects relatedTarget to prevent flicker when focus moves
 * between descendants inside the trigger element. Position is recalculated on
 * window resize, scroll (capture), and trigger/tooltip ResizeObserver changes.
 * Updated: replaced hardcoded zIndex 9999 with Z_INDEX.POPOVER from shared constants.
 */

import { ReactNode, useState, useRef, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Z_INDEX } from '../../constants/zIndex';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipColor = 'dark' | 'light' | 'primary';

interface TooltipProps {
  children: ReactNode;
  text: string;
  position?: TooltipPosition;
  color?: TooltipColor;
  className?: string;
}

interface ResolvedState {
  top: number;
  left: number;
  actualPosition: TooltipPosition;
  ready: boolean;
}

const OFFSET = 8;
const VIEWPORT_MARGIN = 8;

const OPPOSITE: Record<TooltipPosition, TooltipPosition> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

function computeCoords(
  triggerRect: DOMRect,
  tw: number,
  th: number,
  pos: TooltipPosition
): { top: number; left: number } {
  switch (pos) {
    case 'top':
      return {
        top: triggerRect.top - th - OFFSET,
        left: triggerRect.left + triggerRect.width / 2 - tw / 2,
      };
    case 'bottom':
      return {
        top: triggerRect.bottom + OFFSET,
        left: triggerRect.left + triggerRect.width / 2 - tw / 2,
      };
    case 'left':
      return {
        top: triggerRect.top + triggerRect.height / 2 - th / 2,
        left: triggerRect.left - tw - OFFSET,
      };
    case 'right':
      return {
        top: triggerRect.top + triggerRect.height / 2 - th / 2,
        left: triggerRect.right + OFFSET,
      };
  }
}

function resolvePosition(
  triggerRect: DOMRect,
  tooltipRect: DOMRect,
  preferred: TooltipPosition
): { top: number; left: number; actualPosition: TooltipPosition } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = tooltipRect.width;
  const th = tooltipRect.height;

  const fits = (top: number, left: number) =>
    top >= VIEWPORT_MARGIN &&
    left >= VIEWPORT_MARGIN &&
    top + th <= vh - VIEWPORT_MARGIN &&
    left + tw <= vw - VIEWPORT_MARGIN;

  const order: TooltipPosition[] = [
    ...new Set<TooltipPosition>([
      preferred,
      OPPOSITE[preferred],
      'top',
      'bottom',
      'left',
      'right',
    ]),
  ];

  for (const pos of order) {
    const { top, left } = computeCoords(triggerRect, tw, th, pos);
    if (fits(top, left)) {
      return { top, left, actualPosition: pos };
    }
  }

  const { top, left } = computeCoords(triggerRect, tw, th, preferred);
  return {
    top: Math.max(VIEWPORT_MARGIN, Math.min(top, vh - th - VIEWPORT_MARGIN)),
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, vw - tw - VIEWPORT_MARGIN)),
    actualPosition: preferred,
  };
}

export function Tooltip({
  children,
  text,
  position = 'top',
  color = 'dark',
  className = '',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [resolved, setResolved] = useState<ResolvedState>({
    top: 0,
    left: 0,
    actualPosition: position,
    ready: false,
  });

  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    const recalculate = () => {
      if (!trigger || !tooltip) return;
      const triggerRect = trigger.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();
      const result = resolvePosition(triggerRect, tooltipRect, position);
      setResolved({ ...result, ready: true });
    };

    recalculate();

    window.addEventListener('resize', recalculate);
    window.addEventListener('scroll', recalculate, true);

    const ro = new ResizeObserver(recalculate);
    ro.observe(trigger);
    ro.observe(tooltip);

    return () => {
      window.removeEventListener('resize', recalculate);
      window.removeEventListener('scroll', recalculate, true);
      ro.disconnect();
    };
  }, [isVisible, position]);

  const handleMouseEnter = () => {
    setResolved(prev => ({ ...prev, ready: false }));
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    setResolved(prev => ({ ...prev, ready: false }));
  };

  const colorClasses: Record<TooltipColor, string> = {
    dark: 'bg-neutral-900 text-white',
    light: 'bg-white text-neutral-900 border border-neutral-200',
    primary: 'bg-primary-600 text-white',
  };

  const arrowBgClass: Record<TooltipColor, string> = {
    dark: 'bg-neutral-900',
    light: 'bg-white',
    primary: 'bg-primary-600',
  };

  const arrowPositionClass: Record<TooltipPosition, string> = {
    top: 'bottom-[-4px] left-1/2 -translate-x-1/2',
    bottom: 'top-[-4px] left-1/2 -translate-x-1/2',
    left: 'right-[-4px] top-1/2 -translate-y-1/2',
    right: 'left-[-4px] top-1/2 -translate-y-1/2',
  };

  const animationClass: Record<TooltipPosition, string> = {
    top: 'animate-tooltip-top',
    bottom: 'animate-tooltip-bottom',
    left: 'animate-tooltip-left',
    right: 'animate-tooltip-right',
  };

  const tooltipNode = (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      style={{
        position: 'fixed',
        top: resolved.top,
        left: resolved.left,
        zIndex: Z_INDEX.POPOVER,
        visibility: resolved.ready ? 'visible' : 'hidden',
      }}
      className={`
        px-3 py-2 text-xs font-medium rounded-md shadow-soft-lg whitespace-nowrap
        ${colorClasses[color]}
        ${resolved.ready ? animationClass[resolved.actualPosition] : ''}
      `}
    >
      {text}
      <div
        className={`
          absolute w-2 h-2 rotate-45
          ${arrowBgClass[color]}
          ${arrowPositionClass[resolved.actualPosition]}
        `}
      />
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={(e) => {
        const related = e.relatedTarget as Node | null;
        if (related && e.currentTarget.contains(related)) return;
        handleMouseLeave();
      }}
      aria-describedby={isVisible ? tooltipId : undefined}
    >
      {children}
      {isVisible && createPortal(tooltipNode, document.body)}
    </div>
  );
}
